import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plug, Copy, RefreshCw, ShieldCheck, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function IntegrationsPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'Owner' || user?.role === 'Admin';
  const [secret, setSecret] = useState('');
  const [reveal, setReveal] = useState(false);
  const [rotating, setRotating] = useState(false);

  // Absolute URL that external systems will POST to
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const webhookUrl = `${backendUrl}/api/webhooks/leads`;

  const load = async () => {
    try {
      const r = await api.get('/settings/webhooks');
      setSecret(r.data.leads_secret || '');
    } catch (e) {
      toast.error('Failed to load webhook settings');
    }
  };

  useEffect(() => { if (canManage) load(); }, [canManage]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error('Copy failed — please select and copy manually'),
    );
  };

  const rotate = async () => {
    if (!window.confirm('Regenerate the webhook secret? Any system still using the OLD secret will stop working until updated.')) return;
    setRotating(true);
    try {
      const r = await api.post('/settings/webhooks/rotate');
      setSecret(r.data.leads_secret);
      toast.success('New secret generated. Update it in your website / Lovable now.');
    } catch (e) {
      toast.error('Rotation failed');
    }
    setRotating(false);
  };

  const masked = secret ? secret.slice(0, 6) + '•'.repeat(24) + secret.slice(-4) : '';

  const samplePayload = `{
  "name": "Priya Sharma",
  "email": "priya@example.com",
  "phone": "+91 9876543210",
  "company": "ABC Corp",
  "service": "LED Van",
  "message": "Interested in Diwali campaign",
  "source": "Website Contact Form"
}`;

  const curlExample = `curl -X POST "${webhookUrl}" \\
  -H "Content-Type: application/json" \\
  -H "X-Webhook-Secret: ${secret || 'YOUR_SECRET_HERE'}" \\
  -d '${samplePayload.replace(/\n/g, ' ').replace(/\s+/g, ' ')}'`;

  const lovableSnippet = `// In your Lovable form submit handler:
const res = await fetch("${webhookUrl}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-Webhook-Secret": "${secret || 'YOUR_SECRET_HERE'}"
  },
  body: JSON.stringify({
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    company: formData.company,
    service: formData.service,   // optional — one of the 15 services
    message: formData.message,
    source: "Website Contact Form"
  })
});`;

  if (!canManage) {
    return (
      <div>
        <PageHeader icon={Plug} title="Integrations" subtitle="Manage third-party connections." />
        <Card className="shine-card p-10 text-center text-slate-400 rounded-xl">
          Only Owner / Admin can manage integrations.
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        icon={Plug}
        title="Integrations"
        subtitle="Connect your website & external systems to Shine Ops Hub."
      />

      <Card className="shine-card p-5 rounded-xl mb-4">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <ShieldCheck className="h-5 w-5 text-amber-300" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white">Website Leads Webhook</h3>
            <p className="text-sm text-slate-400 mt-1">
              Send lead form submissions from your Lovable-built website (or any external form) directly
              into the Leads module. Duplicate check by company name — repeat enquiries auto-link to the
              existing client. Owner + Admin + Sales get an in-app notification for every new lead.
            </p>
          </div>
        </div>
      </Card>

      {/* Webhook URL */}
      <Card className="shine-card p-5 rounded-xl mb-4">
        <div className="text-xs uppercase tracking-wider text-amber-200 mb-2">Webhook URL (POST)</div>
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={webhookUrl}
            className="bg-[#0f2240] border-amber-500/20 font-mono text-xs"
            data-testid="webhook-url"
          />
          <Button
            variant="outline"
            onClick={() => copy(webhookUrl, 'Webhook URL')}
            className="border-amber-500/40 text-amber-200 flex-shrink-0"
            data-testid="webhook-url-copy"
          >
            <Copy className="h-4 w-4 mr-1" /> Copy
          </Button>
        </div>
      </Card>

      {/* Webhook Secret */}
      <Card className="shine-card p-5 rounded-xl mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-amber-200">
            Webhook Secret — send as header <code className="text-emerald-300 font-mono normal-case">X-Webhook-Secret</code>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setReveal(!reveal)}
            className="text-slate-400 hover:text-amber-300"
            data-testid="webhook-secret-reveal"
          >
            {reveal ? <><EyeOff className="h-4 w-4 mr-1" /> Hide</> : <><Eye className="h-4 w-4 mr-1" /> Reveal</>}
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={reveal ? secret : masked}
            className="bg-[#0f2240] border-amber-500/20 font-mono text-xs"
            data-testid="webhook-secret"
          />
          <Button
            variant="outline"
            onClick={() => copy(secret, 'Secret')}
            className="border-amber-500/40 text-amber-200 flex-shrink-0"
            data-testid="webhook-secret-copy"
            disabled={!secret}
          >
            <Copy className="h-4 w-4 mr-1" /> Copy
          </Button>
          <Button
            variant="outline"
            onClick={rotate}
            disabled={rotating}
            className="border-red-500/40 text-red-300 hover:bg-red-500/10 flex-shrink-0"
            data-testid="webhook-secret-rotate"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${rotating ? 'animate-spin' : ''}`} /> Rotate
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Keep this secret private. Anyone with the URL + secret can create leads in your CRM.
          Rotate if you suspect it's been leaked — external systems will need the new value.
        </p>
      </Card>

      {/* Payload spec */}
      <Card className="shine-card p-5 rounded-xl mb-4">
        <div className="text-xs uppercase tracking-wider text-amber-200 mb-3">Accepted Fields</div>
        <div className="grid sm:grid-cols-2 gap-2 text-xs">
          {[
            { name: 'name', desc: 'Full name (required — falls back to email/phone if missing)', aliases: 'full_name, customer_name, contact_name' },
            { name: 'email', desc: 'Email address', aliases: 'email_address, mail' },
            { name: 'phone', desc: 'Phone / WhatsApp', aliases: 'mobile, phone_number, contact, whatsapp' },
            { name: 'company', desc: 'Company / organisation', aliases: 'company_name, organization, business' },
            { name: 'service', desc: 'Which service they enquired about', aliases: 'service_enquired, interested_in, product' },
            { name: 'message', desc: 'Enquiry / notes', aliases: 'notes, query, enquiry, requirements' },
            { name: 'source', desc: 'Where the lead came from', aliases: 'utm_source, form_source, origin' },
            { name: 'budget', desc: 'Estimated value (INR)', aliases: 'value, estimated_value, amount' },
          ].map((f) => (
            <div key={f.name} className="bg-[#0f2240] border border-amber-500/15 rounded-md p-2.5">
              <div className="font-mono text-emerald-300 font-bold">{f.name}</div>
              <div className="text-slate-300 mt-0.5">{f.desc}</div>
              <div className="text-slate-500 text-[10px] mt-1">Aliases: {f.aliases}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Lovable / JS snippet */}
      <Card className="shine-card p-5 rounded-xl mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-amber-200">Lovable / JavaScript</div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => copy(lovableSnippet, 'Snippet')}
            className="border-amber-500/40 text-amber-200"
            data-testid="lovable-copy"
          >
            <Copy className="h-4 w-4 mr-1" /> Copy code
          </Button>
        </div>
        <pre className="bg-[#0a1628] border border-amber-500/15 rounded-md p-3 text-[11px] text-slate-200 font-mono overflow-x-auto whitespace-pre">
{lovableSnippet}
        </pre>
      </Card>

      {/* curl */}
      <Card className="shine-card p-5 rounded-xl mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-amber-200">Test with curl</div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => copy(curlExample, 'curl command')}
            className="border-amber-500/40 text-amber-200"
            data-testid="curl-copy"
          >
            <Copy className="h-4 w-4 mr-1" /> Copy curl
          </Button>
        </div>
        <pre className="bg-[#0a1628] border border-amber-500/15 rounded-md p-3 text-[11px] text-slate-200 font-mono overflow-x-auto whitespace-pre">
{curlExample}
        </pre>
      </Card>

      {/* Zapier / other tools note */}
      <Card className="shine-card p-4 rounded-xl">
        <div className="flex items-start gap-3">
          <ExternalLink className="h-4 w-4 text-amber-300 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-slate-300">
            <div className="text-white font-semibold mb-1">Zapier / Make / Meta Lead Ads / IndiaMart</div>
            The webhook accepts any tool that can POST JSON to a URL with a custom header. Configure your
            source to send <code className="text-emerald-300 font-mono">POST {webhookUrl}</code> with header
            <code className="text-emerald-300 font-mono"> X-Webhook-Secret</code> set to the secret above. Map your
            source's fields to the aliases listed under "Accepted Fields".
          </div>
        </div>
      </Card>
    </div>
  );
}
